import React, { useEffect, useState } from "react"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"
import { useGQL } from "../hooks/useGQL.js"
import { Button, TextField } from "@shopify/polaris"
import { LoadingToast } from "./LoadingToast.jsx"
import { useTranslation } from "react-i18next"

const noImageFound = (
	<svg
		width="100%"
		height="100%"
		viewBox="0 0 20 20"
		className="Polaris-Icon__Svg_375hu"
		focusable="false"
		aria-hidden="true"
	>
		<path d="M12.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"></path>
		<path
			fillRule="evenodd"
			d="M9.018 3.5h1.964c.813 0 1.469 0 2 .043.546.045 1.026.14 1.47.366a3.75 3.75 0 0 1 1.64 1.639c.226.444.32.924.365 1.47.043.531.043 1.187.043 2v1.964c0 .813 0 1.469-.043 2-.045.546-.14 1.026-.366 1.47a3.75 3.75 0 0 1-1.639 1.64c-.444.226-.924.32-1.47.365-.531.043-1.187.043-2 .043h-1.964c-.813 0-1.469 0-2-.043-.546-.045-1.026-.14-1.47-.366a3.75 3.75 0 0 1-1.64-1.639c-.226-.444-.32-.924-.365-1.47-.043-.531-.043-1.187-.043-2v-1.964c0-.813 0-1.469.043-2 .045-.546.14-1.026.366-1.47a3.75 3.75 0 0 1 1.639-1.64c.444-.226.924-.32 1.47-.365.531-.043 1.187-.043 2-.043Zm-1.877 1.538c-.454.037-.715.107-.912.207a2.25 2.25 0 0 0-.984.984c-.1.197-.17.458-.207.912-.037.462-.038 1.057-.038 1.909v1.428l.723-.867a1.75 1.75 0 0 1 2.582-.117l2.695 2.695 1.18-1.18a1.75 1.75 0 0 1 2.604.145l.216.27v-2.374c0-.852 0-1.447-.038-1.91-.037-.453-.107-.714-.207-.911a2.25 2.25 0 0 0-.984-.984c-.197-.1-.458-.17-.912-.207-.462-.037-1.056-.038-1.909-.038h-1.9c-.852 0-1.447 0-1.91.038Zm-2.103 7.821a7.12 7.12 0 0 1-.006-.08.746.746 0 0 0 .044-.049l1.8-2.159a.25.25 0 0 1 .368-.016l3.226 3.225a.75.75 0 0 0 1.06 0l1.71-1.71a.25.25 0 0 1 .372.021l1.213 1.516c-.021.06-.045.114-.07.165-.216.423-.56.767-.984.983-.197.1-.458.17-.912.207-.462.037-1.056.038-1.909.038h-1.9c-.852 0-1.447 0-1.91-.038-.453-.037-.714-.107-.911-.207a2.25 2.25 0 0 1-.984-.984c-.1-.197-.17-.458-.207-.912Z"
		></path>
	</svg>
)

export const CollectionPicker = ({ open, onCancel, onSelection }) => {
	const authenticatedFetch = useAuthenticatedFetch()
	const gql = useGQL()
	const { t } = useTranslation()

	const [loadingCollections, setLoadingCollections] = useState(false)
	const [filter, setFilter] = useState("")
	const [collections, setCollections] = useState([])
	const [selections, setSelections] = useState([])

	const getCollections = async () => {
		setLoadingCollections(true)
		const res = await gql(`{
			collections(first: 100) {
				nodes {
					id
					title
					image {
						url
					}
				}
			}
		}`)
		setLoadingCollections(false)

		if (res?.data?.collections?.nodes?.length) {
			setCollections(res.data.collections.nodes)
		}
	}

	const handleFilterChange = (change) => {
		setFilter(change)
	}

	const addSelection = async (collection) => {
		const newSelections = [...selections, collection]
		setSelections(newSelections)
		onSelection(newSelections)
	}

	const removeSelection = async (collection) => {
		const newSelections = selections.filter((x) => x.id !== collection.id)
		setSelections(newSelections)
		onSelection(newSelections)
	}

	useEffect(() => {
		if (open) {
			getCollections()
		}
	}, [open])

	if (!open) {
		return null
	}

	const filteredCollections = collections.filter((collection) =>
		collection.title.toLowerCase().includes(filter.toLowerCase()),
	)

	return (
		<div
			style={{
				marginTop: "1rem",
				display: "flex",
				flexDirection: "column",
				gap: "0.5rem",
			}}
		>
			{collections?.length ? (
				<TextField
					value={filter}
					onChange={handleFilterChange}
					placeholder={t(
						"components.collection_picker.filter_collections",
					)}
				/>
			) : (
				""
			)}
			{loadingCollections ? (
				<LoadingToast position="relative">
					{t("components.collection_picker.loading_collections")}
				</LoadingToast>
			) : (
				filteredCollections.map((collection) => {
					return (
						<div
							key={collection.id}
							style={{
								width: "100%",
								display: "flex",
								gap: "0.5rem",
								alignItems: "center",
								padding: "0.5rem",
								paddingLeft: "1rem",
								border: "1px solid #ccc",
								borderRadius: "1.5rem",
								background: "#fff",
								boxShadow:
									"0 1px 1px rgba(0,0,0,0.1), 0 2px 2px rgba(0,0,0,0.1)",
							}}
						>
							<div style={{ width: "1.5rem", height: "1.5rem" }}>
								{collection.image?.url ? (
									<img
										src={collection.image?.url}
										style={{
											width: "100%",
											height: "100%",
											borderRadius: "0.3rem",
										}}
									/>
								) : (
									noImageFound
								)}
							</div>
							<span
								style={{
									fontSize: "0.9rem",
									fontWeight: "500",
								}}
							>
								{collection.title}
							</span>
							<div style={{ marginLeft: "auto" }}>
								{selections.find(
									(x) => x.id === collection.id,
								) ? (
									<Button
										onClick={() =>
											removeSelection(collection)
										}
									>
										{t("common.remove")}
									</Button>
								) : (
									<Button
										onClick={() => addSelection(collection)}
									>
										{t("common.add")}
									</Button>
								)}
							</div>
						</div>
					)
				})
			)}
		</div>
	)
}
